(ns leg.admin
  "Everybody who reads on this server, for the one person who runs it.

  A place and not a dialog: it has its own address (`/admin`, see
  `leg.route`), so it can be opened in a second tab, bookmarked, and left
  again with the browser's own back button. In this window the address bar is
  part of the interface, and a screen that is worth coming back to has to be
  somewhere.

  It lists people and not their reading. Who is here, since when, how many
  feeds they have and which devices they signed in from - never which feeds,
  never which articles. Running the server already means having the files on
  disk; that is no reason to build a window into somebody else's reading.

  Two things can be done from here, and both of them make an account: typing
  somebody's address, or holding the door open for the next address that asks
  (see `Door`). There is nothing to hand over afterwards either way - whoever
  was added signs in the way everybody does, by asking for a code at their
  address."
  (:require-macros [kc.core :refer [def-ui $]])
  (:require [leg.mui :as mui :refer [sx]]
            [leg.page :as page]
            [leg.state :as state]
            [leg.util :as u]))

(def-ui Fact
  "One number about one person, with what it counts under it. Small, because
  a row of these is read as a row and not as five sentences."
  [n label]
  ($ mui/Box {:sx (sx {:minWidth "4.5rem"})}
     ($ :div {:style {:fontWeight 600}} (str n))
     ($ :div {:style {:fontSize ".75rem" :opacity ".7"}} label)))

(def-ui Reader [reader]
  ($ mui/Box {:sx (sx {:display "flex" :alignItems "center" :gap ".75rem"
                       :flexWrap "wrap"
                       :padding ".6rem .25rem"
                       :borderBottom "1px solid" :borderColor "divider"})}
     ($ mui/Box {:sx (sx {:flex "1 1 14rem" :minWidth 0})}
        ($ :div {:style {:overflow "hidden" :textOverflow "ellipsis"
                         :whiteSpace "nowrap" :fontWeight 600}}
           (:reader/email reader))
        ($ :div {:style {:fontSize ".78rem" :opacity ".7"}}
           (str "since " (or (u/stamp (:reader/created reader)) "?")
                (when-let [seen (:reader/seen reader)]
                  (str " - last here " (u/ago seen))))))
     ($ Fact {:n (:reader/feeds reader) :label "feeds"})
     ($ Fact {:n (:reader/clients reader) :label "devices"})
     ;; Who the admin is, is in the server's code, so this is a fact about
     ;; the list and not a switch: there is nothing to press here.
     ($ mui/Box {:sx (sx {:minWidth "4rem"})}
        (when (:reader/admin? reader)
          ($ mui/Chip {:label "admin" :size "small" :variant "outlined"})))))

(def-ui Add
  "Making somebody an account, as one button: the field is in the dialog it
  opens (`leg.dialogs/AddReader`)."
  []
  ($ mui/Box {:sx (sx {:paddingTop ".75rem"})}
     ($ mui/Button {:variant "contained" :disableElevation true
                    :onClick (fn [_] (state/dialog! :add-reader))}
        "Add reader")))

(def-ui Door
  "The other way to add somebody: hold the door open and let them type their
  own address.

  Worth a switch rather than a second field, because the field is the problem
  - adding somebody means spelling out an address that was said over a phone,
  and the one thing nobody gets right that way is somebody else's address.
  Open this, hand them the login screen, and the address that arrives is the
  one they typed.

  Exactly one. The server shuts it the moment a code goes out to an unknown
  address (`leser.door`), and mails here who walked through. It does not
  expire: an open door stays open until it is used or closed, which is why
  this says so plainly while it is."
  [door busy?]
  (let [open? (boolean (:door/open? door))]
    ($ page/Section {:title "The next new reader"}
       ($ mui/Box {:sx (sx {:display "flex" :alignItems "flex-start" :gap ".5rem"
                            :flexWrap "wrap"})}
          ($ mui/Typography {:sx (sx {:flex "1 1 14rem" :minWidth 0
                                      :color "text.secondary" :fontSize ".8rem"})}
             (if open?
               (str "The door is open"
                    (when-let [at (:door/opened door)] (str " since " (u/ago at)))
                    ". The next address that asks for a code becomes a reader"
                    " here, and then it closes. Nothing expires it.")
               (str "Closed. An address nobody added gets no code and no"
                    " account, however often it asks.")))
          ($ mui/Button {:disabled busy?
                         :variant (if open? "outlined" "contained")
                         :color (if open? "error" "primary")
                         :disableElevation true
                         :sx (sx {:flex "none"})
                         :onClick (fn [_] (state/set-door! (not open?)))}
             (if open? "Close the door" "Let the next one in"))))))

(def-ui AdminPane
  "The page. Over the whole window rather than beside it: this is not a
  fourth pane of the reader, it is somewhere else - and it wears the same
  frame as the settings (`leg.page`), because they are the same kind of
  place."
  []
  (let [{:keys [readers door loading? busy?]} (state/use-state)]
    ($ page/Page {:title "Readers" :loading? loading?}
       ($ mui/Box {:sx (sx {:paddingTop ".5rem"})}
          (for [r readers]
            ($ Reader {:key (str (:reader/id r)) :reader r})))
       (when (and (not loading?) (empty? readers))
         ($ mui/Typography {:sx (sx {:color "text.secondary" :paddingTop ".75rem"})}
            "Nobody yet."))
       ($ Add)
       ($ mui/Box {:sx (sx {:paddingTop "1.5rem"})}
          ($ Door {:door door :busy? busy?})))))
